'use client'

import { useState } from 'react'

import cn from 'clsx'
import { checkHighlightedHeading, type TOCHeading } from '@/utils/tocHelpers'

import css from './TOCDesktop.module.css'

import { Box, type BoxProps } from '@/components/Box/Box'
import { HyperLink } from '@/components/hyper-link/hyper-link'
import { useThrottledScroll } from '@/utils/useThrottledScroll'
import { Text } from '@/components/Text/Text'

export type TOCDesktopProps = BoxProps & {
    tableOfContents: TOCHeading[]
    title?: string
    contentMaxHeight?: string
}

export function TOCDesktop({
    title = 'Table of Contents',
    tableOfContents,
    className,
    contentMaxHeight = 'auto',
    ...rest
}: TOCDesktopProps) {
    const [activeHeading, setActiveHeading] = useState<TOCHeading>()

    useThrottledScroll(() => {
        function updateActiveHeading() {
            const heading = checkHighlightedHeading(tableOfContents)
            setActiveHeading(heading)
        }
        updateActiveHeading()
        return updateActiveHeading
    }, [tableOfContents])

    return (
        <Box className={cn(css.tocDesktop, className)} {...rest}>
            <Text size="minor-xs">{title}</Text>

            <ul style={{ maxHeight: contentMaxHeight }}>
                {tableOfContents.map((heading) => (
                    <li
                        key={heading.title}
                        className={
                            activeHeading && activeHeading.id === heading.id
                                ? css.active
                                : undefined
                        }
                    >
                        <HyperLink size="body-xs" href={`#${heading.id}`}>
                            {heading.title}
                        </HyperLink>
                    </li>
                ))}
            </ul>
        </Box>
    )
}
